/*
 * @Author: Ryan
 * @Date: 2026-01-16
 * @Description: 视频横向滚动组件 - 平铺展示，点击按钮滚动
 */
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import { Button } from 'antd';
import Image from 'next/image';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import styles from './index.module.scss';

// 静音图标
const MutedIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="15"
    height="15"
    viewBox="0 0 15 15"
    fill="none"
  >
    <g clipPath="url(#clip0_muted_carousel)">
      <path opacity="0.01" d="M12.9 5.40002H9V9.30002H12.9V5.40002Z" fill="#333333" />
      <mask
        id="mask0_muted_carousel"
        style={{ maskType: 'alpha' }}
        maskUnits="userSpaceOnUse"
        x="9"
        y="5"
        width="4"
        height="5"
      >
        <path d="M12.9 5.40002H9V9.30002H12.9V5.40002Z" fill="#333333" />
      </mask>
      <g mask="url(#mask0_muted_carousel)">
        <path
          d="M12.2204 6.08575L9.6748 8.63134"
          stroke="#333333"
          strokeWidth="1.2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M9.6748 6.08575L12.2204 8.63134"
          stroke="#333333"
          strokeWidth="1.2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </g>
      <path
        d="M7.2002 1.80002V12.6C5.1002 12.6 3.53975 9.85175 3.53975 9.85175H1.8002C1.46882 9.85175 1.2002 9.58313 1.2002 9.25175V5.10326C1.2002 4.77188 1.46882 4.50326 1.8002 4.50326H3.53975C3.53975 4.50326 5.1002 1.80002 7.2002 1.80002Z"
        stroke="#333333"
        strokeWidth="1.2"
        strokeLinejoin="round"
      />
    </g>
    <defs>
      <clipPath id="clip0_muted_carousel">
        <rect width="14.4" height="14.4" fill="white" />
      </clipPath>
    </defs>
  </svg>
);

// 非静音图标
const UnmutedIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="15"
    height="15"
    viewBox="0 0 15 15"
    fill="none"
  >
    <path
      d="M7.19922 1.80002V12.6C5.09922 12.6 3.53877 9.85175 3.53877 9.85175H1.79922C1.46785 9.85175 1.19922 9.58313 1.19922 9.25175V5.10326C1.19922 4.77188 1.46785 4.50326 1.79922 4.50326H3.53877C3.53877 4.50326 5.09922 1.80002 7.19922 1.80002Z"
      stroke="#333333"
      strokeWidth="1.2"
      strokeLinejoin="round"
    />
    <path
      d="M9.59961 4.5C9.78657 4.66695 9.95604 4.85391 10.1048 5.05764C10.5412 5.65512 10.7996 6.39669 10.7996 7.2C10.7996 7.99635 10.5457 8.73201 10.1161 9.32679C9.96465 9.53655 9.79134 9.72879 9.59961 9.9"
      stroke="#333333"
      strokeWidth="1.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export interface VideoItem {
  id: string;
  videoUrl: string;
  posterUrl: string;
  alt?: string;
}

export interface VideoCarouselProps {
  /** 视频列表 */
  videos: VideoItem[];
  /** 是否自动播放 */
  autoPlay?: boolean;
  /** 自定义类名 */
  className?: string;
}

const VideoCarousel: React.FC<VideoCarouselProps> = ({
  videos,
  autoPlay = true,
  className = '',
}) => {
  const [playingIndex, setPlayingIndex] = useState<number | null>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const videoRefs = useRef<{ [key: number]: HTMLVideoElement | null }>({});

  // 检查滚动状态
  const checkScrollState = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const { scrollLeft, scrollWidth, clientWidth } = container;
    setCanScrollLeft(scrollLeft > 1);
    setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
  }, []);

  // 初始化和监听滚动
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    checkScrollState();
    container.addEventListener('scroll', checkScrollState);
    window.addEventListener('resize', checkScrollState);

    return () => {
      container.removeEventListener('scroll', checkScrollState);
      window.removeEventListener('resize', checkScrollState);
    };
  }, [checkScrollState]);

  // 滚动到指定方向
  const scroll = useCallback((direction: 'left' | 'right') => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const scrollAmount = container.clientWidth / 3;
    const targetScroll =
      direction === 'left'
        ? container.scrollLeft - scrollAmount
        : container.scrollLeft + scrollAmount;

    container.scrollTo({
      left: targetScroll,
      behavior: 'smooth',
    });
  }, []);

  // 处理声音按钮点击
  const handleSoundClick = (index: number, e: React.MouseEvent) => {
    e.stopPropagation();

    // 如果点击的是当前播放的视频，则静音
    if (playingIndex === index) {
      const video = videoRefs.current[index];
      if (video) {
        video.muted = true;
      }
      setPlayingIndex(null);
      return;
    }

    // 静音其他所有视频
    Object.values(videoRefs.current).forEach((video) => {
      if (video) {
        video.muted = true;
      }
    });

    // 取消静音当前视频
    const video = videoRefs.current[index];
    if (video) {
      video.muted = false;
      video.play().catch(() => {});
      setPlayingIndex(index);
    }
  };

  // 自动播放视频
  useEffect(() => {
    if (autoPlay) {
      Object.values(videoRefs.current).forEach((video) => {
        if (video) {
          video.play().catch(() => {});
        }
      });
    }
  }, [autoPlay]);

  if (videos.length === 0) {
    return null;
  }

  return (
    <div className={`${styles.wrapper} ${className}`}>
      {/* 左按钮 */}
      <Button
        type="text"
        shape="circle"
        icon={<LeftOutlined />}
        className={`${styles.navButton} ${styles.navPrev}`}
        onClick={() => scroll('left')}
        disabled={!canScrollLeft}
        aria-label="Previous"
      />

      {/* 视频滚动容器 */}
      <div className={styles.scrollContainer} ref={scrollContainerRef}>
        <div className={styles.videoList}>
          {videos.map((video, index) => (
            <div key={video.id} className={styles.videoItem}>
              <div className={styles.videoContainer}>
                {video.videoUrl ? (
                  <video
                    ref={(el) => {
                      videoRefs.current[index] = el;
                    }}
                    src={video.videoUrl}
                    poster={video.posterUrl}
                    className={styles.video}
                    loop
                    playsInline
                    muted
                    autoPlay={autoPlay}
                    preload="metadata"
                  />
                ) : video.posterUrl ? (
                  <Image
                    src={video.posterUrl}
                    alt={video.alt || ''}
                    fill
                    className={styles.video}
                  />
                ) : (
                  <div className={styles.placeholder} />
                )}

                {/* 声音按钮 - 仅在有视频时显示 */}
                {video.videoUrl && (
                  <button
                    type="button"
                    className={styles.soundButton}
                    onClick={(e) => handleSoundClick(index, e)}
                    aria-label={playingIndex === index ? 'Mute' : 'Unmute'}
                  >
                    {playingIndex === index ? <UnmutedIcon /> : <MutedIcon />}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* 右按钮 */}
      <Button
        type="text"
        shape="circle"
        icon={<RightOutlined />}
        className={`${styles.navButton} ${styles.navNext}`}
        onClick={() => scroll('right')}
        disabled={!canScrollRight}
        aria-label="Next"
      />
    </div>
  );
};

export default VideoCarousel;
